import { usePathname } from 'expo-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, ICONS } from './icon';
import { NavLink } from './nav-link';
import { localePath } from '../lib/format';
import { useLanguageHref } from '../lib/use-language-href';
import { SITE_UI } from '@site-ui';
import type { SiteUi } from '../lib/site-ui';
import { LOCALE_NAMES, SUPPORTED_LOCALES } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';

import css from './site-header.module.css';

// The header nav is the site's (SITE_UI.nav). The Saved and Account icons
// lead to "coming soon" pages — there are no accounts yet (what they should do
// is an open question, docs/monorepo-plan.md stage 5.5). `transparent`: no
// background or border, over the page's hero (Page `underHeader`).
export function SiteHeader({ transparent }: { transparent?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const pathname = usePathname();
  const languageHref = useLanguageHref();
  const menuRef = useRef<HTMLDetailsElement>(null);
  // Widened to the SiteUi shape: headerCorner is absent from dubai's literal type.
  const { headerCorner } = SITE_UI as SiteUi;

  // Slides the drawer out (the CSS animation of [data-closing]) and only then
  // closes the <details> — closing it at once would cut the sheet off. The
  // timeout covers a missing animationend (reduced motion, hidden tab).
  const closeMenu = () => {
    const menu = menuRef.current;
    if (!menu?.open || menu.dataset.closing) return;
    menu.dataset.closing = 'true';
    const done = () => {
      delete menu.dataset.closing;
      menu.open = false;
    };
    menu.addEventListener('animationend', done, { once: true });
    setTimeout(done, 300);
  };

  // A link in the drawer navigates client-side; close it with the new page.
  useEffect(() => {
    if (menuRef.current) menuRef.current.open = false;
  }, [pathname]);

  const links = SITE_UI.nav.map((item) => (
    <NavLink key={item.path} href={localePath(locale, item.path)} className={css.navLink}>
      {t(item.key)}
    </NavLink>
  ));

  const languages = (className: string) =>
    SUPPORTED_LOCALES.map((l) => (
      <a
        key={l}
        href={languageHref(l)}
        hrefLang={l}
        lang={l}
        aria-current={l === locale ? 'true' : undefined}
        className={className}>
        {LOCALE_NAMES[l]}
      </a>
    ));

  return (
    <header className={`${css.header} ${transparent ? css.transparent : ''}`}>
      {headerCorner ? (
        <img
          className={css.corner}
          src={headerCorner.image}
          width={headerCorner.width}
          height={headerCorner.height}
          alt=""
          decoding="async"
        />
      ) : null}
      <div className={css.row}>
        <NavLink href={localePath(locale)} aria-label={t('a11y.home')} className={css.lockup}>
          <SITE_UI.BrandLockup large />
        </NavLink>

        <nav aria-label={t('a11y.mainNav')} className={css.nav}>
          {links}
        </nav>

        <div className={css.actions}>
          {/* Search and Saved are desktop-only: on phones the row has room for
              the language menu and the burger only. */}
          <NavLink
            href={localePath(locale, 'tours')}
            aria-label={t('a11y.search')}
            className={`${css.iconButton} ${css.desktopOnly}`}>
            <Icon d={ICONS.search} size={22} />
          </NavLink>

          {/* <details> menus open without JS and render the same on the
              server and the client — no hydration-dependent state. The
              language menu is on every width; Account is desktop-only, as on
              the Mobile board — phones get it in the menu. */}
          <details className={css.menu}>
            <summary
              className={css.langSummary}
              aria-label={t('a11y.language', { name: LOCALE_NAMES[locale] })}>
              {locale.toUpperCase()} <Icon d={ICONS.chevronDown} size={12} strokeWidth={2.2} />
            </summary>
            <div className={css.dropdown}>{languages(css.dropdownLink)}</div>
          </details>

          <NavLink
            href={localePath(locale, 'saved')}
            aria-label={t('a11y.saved')}
            className={`${css.iconButton} ${css.desktopOnly}`}>
            <Icon d={ICONS.heart} size={22} />
          </NavLink>

          <NavLink
            href={localePath(locale, 'account')}
            aria-label={t('a11y.account')}
            className={`${css.iconButton} ${css.desktopOnly}`}>
            <Icon d={ICONS.user} size={22} />
          </NavLink>

          {/* Phones: a sheet over a dimmed page that slides up into place
              and back down, as on riviera-todo.com. Still a <details>, so it opens without JS;
              the backdrop, the close button and Escape close it with JS. */}
          <details
            ref={menuRef}
            className={`${css.menu} ${css.drawer} ${css.mobileOnly}`}
            onKeyDown={(e) => e.key === 'Escape' && closeMenu()}>
            <summary className={css.iconButton} aria-label={t('a11y.menu')}>
              <Icon d={ICONS.menu} size={24} />
            </summary>
            <div className={css.layer}>
              <div className={css.backdrop} onClick={closeMenu} aria-hidden="true" />
              <div className={css.mobileMenu}>
                <div className={css.sheetHeader}>
                  <NavLink href={localePath(locale)} aria-label={t('a11y.home')} className={css.lockup}>
                    <SITE_UI.BrandLockup />
                  </NavLink>
                  <button type="button" className={css.iconButton} aria-label={t('a11y.closeMenu')} onClick={closeMenu}>
                    <Icon d={ICONS.close} size={24} />
                  </button>
                </div>
                <nav aria-label={t('a11y.mainNav')} className={css.mobileNav}>
                  {links}
                  <NavLink href={localePath(locale, 'account')} className={css.navLink}>
                    {t('a11y.account')}
                  </NavLink>
                </nav>
                <div className={css.mobileLanguages}>{languages(css.mobileLanguage)}</div>
              </div>
            </div>
          </details>
        </div>
      </div>
    </header>
  );
}
